import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { FilterForm } from '@/components/filters/FilterForm'

import { MultiSelect } from '@/components/forms/MultiSelect'
import { InfoTooltip } from '@/components/tooltips/InfoTooltip'
import { FormField } from '@/components/forms/FormField'
import { setFilter } from '@/reducers/scorecards/scorecards.redux'

export const ViewScoresOptions = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const isQACopilot = location.pathname.startsWith('/qa-copilot')

  const { filters } = useSelector((state) => state.scorecards)
  const metricOptions = [
    { value: 'overall_scores', label: 'Overall Score', accessor: 'percentage_score' },
    { value: 'sections', label: 'Section Score' },
  ]

  if (!isQACopilot) {
    metricOptions.push({ value: 'measures', label: 'Measure Score' })
  }

  metricOptions.push({ value: 'criteria', label: 'Criteria Score' })

  const groupByOptions = [
    { value: 'agents', label: 'Agents' },
    { value: 'tags', label: 'Tags' },
    { value: 'calls', label: 'Calls' },
  ]

  const handleGroupBySelect = (value) => {
    if (value) {
      dispatch(setFilter('groupBy', value))
    } else {
      dispatch(setFilter('groupBy', []))
    }
  }

  const handleMetricSelect = (value) => {
    if (value) {
      dispatch(setFilter('metrics', value))
    } else {
      dispatch(setFilter('metrics', []))
    }
  }

  const isGroupByOptionDisabled = (value) => {
    if (isEmpty(filters.groupBy)) return false

    const disableMap = {
      agents: ['tags', 'calls'],
      tags: ['agents', 'calls'],
      calls: ['agents', 'tags'],
    }

    return filters.groupBy.some((option) => disableMap[option.value]?.includes(value.value))
  }

  const isMetricOptionDisabled = (value) => {
    if (!isEmpty(filters.scorecards) && filters.scorecards.length > 1) {
      const multiScorecardDisabledFilters = ['sections', 'measures', 'criteria']
      return multiScorecardDisabledFilters.includes(value.value)
    }
    return false
  }

  return (
    <section data-testid="view-score-options">
      <h3 className="flex-align-center small-gap mt">
        Display Options
        <InfoTooltip
          content={
            <>
              <p>At a minimum, at least one display metric must be selected</p>
              <p>
                Display metrics are quantitative measurements. For example, the display metric,
                “Overall Score” is the average of scores
              </p>
              <p>
                Display groupings are attributes of your data. For example, grouping by “Agents”
                will show agents as one selected column and each row as a unique agent
              </p>
            </>
          }
        />
      </h3>
      <FilterForm medium sticky={false}>
        <FormField required data-testid="advanced-exports-display-metrics-filter">
          <label id="metrics-dropdown">Show Me Metrics</label>
          <div data-testid="metrics-dropdown">
            <MultiSelect
              placeholder="Select Display Metric"
              aria-labelledby="metrics-dropdown"
              options={metricOptions}
              value={filters?.metrics}
              onChange={(value) => handleMetricSelect(value)}
              isOptionDisabled={(option) => isMetricOptionDisabled(option)}
            />
          </div>
        </FormField>
        <FormField required>
          <label id="group-by-dropdown">Grouped By</label>
          <div data-testid="group-by-dropdown">
            <MultiSelect
              placeholder="Select Display Grouping"
              aria-labelledby="group-by-dropdown"
              options={groupByOptions}
              value={filters?.groupBy}
              onChange={(value) => handleGroupBySelect(value)}
              isOptionDisabled={(option) => isGroupByOptionDisabled(option)}
            />
          </div>
        </FormField>
      </FilterForm>
    </section>
  )
}
